import React, { ChangeEvent, useEffect, useRef, useState } from 'react';

import { Font } from '@pdfme/common';
import { generate } from '@pdfme/generator';
import {
  barcodes,
  checkbox,
  dateTime,
  ellipse,
  image,
  line,
  radioGroup,
  rectangle,
  select,
  table,
  text,
  time,
} from '@pdfme/schemas';
import {
  Button,
  IconButton,
  Loader,
  MenuItem,
  SaveIcon,
  Select,
  SelectChangeEvent,
  Tooltip,
} from 'components';
import { useNotifications } from 'hooks';
import {
  Add,
  DocumentDownload,
  DocumentUpload,
  Eye,
  RefreshCircle,
} from 'iconsax-react';

import {
  getSampleCheckTemplate,
  getSampleCheckTemplateWhitePaper,
  previewSampleData,
} from './sampleData';

import {
  Designer,
  Template,
  cloneDeep,
  getInputFromTemplate,
  useParams,
  useSelector,
  useTheme,
  useTranslation,
} from 'third-party';

import { useBank } from 'modules/bank';
import { useSettings } from 'modules/setting';

import { bankSelectors, settingSelectors } from 'selectors';

import { readFile, removeNullFields } from 'utils/helper';

import { APIBank, PrintType } from 'types/api';

const headerHeight = 20;
const controllerHeight = 60;

// Build display data merging sample defaults with real bank fields
const buildDisplayData = (bank: APIBank | null): Record<string, string> => {
  if (!bank) return previewSampleData as Record<string, string>;
  const address = `${bank.address}\n${bank.city}, ${bank.state} ${bank.zip}`;
  const bankInfo = `\u2446${bank.routing}\u2446  ${bank.account}\u2446  001001`;
  return {
    ...(previewSampleData as Record<string, string>),
    bankName: bank.bankName,
    companyName: bank.companyName,
    companyAddress: address,
    compnayAddressMiddle: address,
    bankInfo,
  };
};

// Replace {{fieldName}} placeholders with display values for designer canvas
const toDisplayTemplate = (
  tmpl: Template,
  data: Record<string, string>,
): Template => ({
  ...tmpl,
  schemas: tmpl.schemas.map(schemaArray =>
    schemaArray.map(schema => {
      const sampleValue = data[schema.name];
      if (
        sampleValue !== undefined &&
        schema.content === `{{${schema.name}}}`
      ) {
        return { ...schema, content: sampleValue };
      }
      return schema;
    }),
  ),
});

// Restore {{fieldName}} patterns from sample values before saving
const fromDisplayTemplate = (tmpl: Template): Template => ({
  ...tmpl,
  schemas: tmpl.schemas.map(schemaArray =>
    schemaArray.map(schema => {
      if (
        (previewSampleData as Record<string, string>)[schema.name] !== undefined
      ) {
        return { ...schema, content: `{{${schema.name}}}` };
      }
      return schema;
    }),
  ),
});

const defaultTemplate = toDisplayTemplate(
  getSampleCheckTemplate(),
  previewSampleData as Record<string, string>,
);

// Customize QR code default content
const customQrcode = {
  ...barcodes.qrcode,
  propPanel: {
    ...barcodes.qrcode.propPanel,
    defaultSchema: {
      ...barcodes.qrcode.propPanel.defaultSchema,
      content: 'https://app.easyecheck.online/',
    },
  },
};

export const plugins = {
  text,
  image,
  qrcode: customQrcode,
  line,
  table,
  rectangle,
  ellipse,
  dateTime,
  time,
  select,
  checkbox,
  radioGroup,
};

const actionButtonStyle = {
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
};

export const font: Font = {
  Roboto: {
    data: 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Me5Q.ttf',
    fallback: true,
  },
  RobotoBold: {
    data: 'https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmWUlvAw.ttf',
  },
  liberationSans: {
    data: 'https://fonts.cdnfonts.com/s/277/LiberationSans-Regular.woff',
  },
  liberationSansBold: {
    data: 'https://fonts.cdnfonts.com/s/277/LiberationSans-Bold.woff',
  },
  micrEncoding: {
    data: 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Me5Q.ttf',
  },
};

export const CheckDesigner = () => {
  const designerRef = useRef<HTMLDivElement | null>(null);
  const designer = useRef<Designer | null>(null);
  const theme = useTheme();

  const [checkType, setCheckType] = useState(PrintType.Check);
  const [template, setTemplate] = useState<Template>(defaultTemplate);
  const [, setSmallDisplay] = useState(true);
  const [prevDesignerRef, setPrevDesignerRef] = useState<Designer | null>(null);
  const { error, success } = useNotifications();
  const { t } = useTranslation();

  const loadCheckTemplatePending = useSelector(
    settingSelectors.loadCheckTemplatePending,
  );

  const saveCheckTemplatePending = useSelector(
    settingSelectors.saveCheckTemplatePending,
  );

  const ref = useRef<any>(null);
  const { bankId } = useParams();

  const bank = useSelector(bankSelectors.bank);

  const { saveCheckTemplate, loadCheckTemplate } = useSettings();
  const { loadBank } = useBank();

  useEffect(() => {
    if (bankId) {
      loadBank(bankId);
    }
  }, [bankId, loadBank]);

  useEffect(() => {
    const newTemplate = toDisplayTemplate(
      checkType == PrintType.WhitePaper
        ? getSampleCheckTemplateWhitePaper()
        : getSampleCheckTemplate(),
      buildDisplayData(bank),
    );
    setTemplate(newTemplate);
    designer.current?.updateTemplate(newTemplate);
  }, [checkType, bank]);

  useEffect(() => {
    if (bankId) {
      loadCheckTemplate(bankId, checkType).then((it: any) => {
        setSmallDisplay(false);
        if (it.payload == null) {
          setTemplate(defaultTemplate);
          designer.current?.updateTemplate(defaultTemplate);
          return;
        }

        const cleanPayload = removeNullFields(it.payload);
        const displayPayload = toDisplayTemplate(
          cleanPayload,
          buildDisplayData(bank),
        );
        setTemplate(displayPayload);
        designer.current?.updateTemplate(displayPayload);
      });
    }
  }, [bankId, checkType, loadCheckTemplate, bank]);

  const buildDesigner = () => {
    if (designerRef.current && template) {
      designer.current = new Designer({
        domContainer: designerRef.current,
        template,
        plugins: plugins,
        options: {
          font: font,
          lang: 'en',
          theme: {
            token: {
              colorPrimary: theme.custom.palette.primary300,
              colorPrimaryHover: theme.custom.palette.primary800,
            },
          },
        },
      });
      designer.current.onSaveTemplate(downloadTemplate);
      designer.current.onChangeTemplate(setTemplate);
    }
  };

  const changeBasePdf = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!designer.current || !file) {
      return;
    }

    readFile(file, 'dataURL').then(basePdf => {
      designer?.current?.updateTemplate(
        Object.assign(cloneDeep(template), { basePdf }),
      );
    });
  };

  const loadTemplate = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!designer.current || !file) {
      return;
    }

    readFile(file, 'text').then(jsonStr => {
      const loadedTemplate: Template = toDisplayTemplate(
        JSON.parse(jsonStr as string) as Template,
        buildDisplayData(bank),
      );
      try {
        designer.current?.updateTemplate(loadedTemplate);
      } catch {
        error('unable to load file');
      }
    });
  };

  const downloadTemplate = () => {
    const json = fromDisplayTemplate(
      designer.current?.getTemplate() ?? template,
    );
    const title = 'template';
    if (typeof window !== 'undefined') {
      const blob = new Blob([JSON.stringify(json)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title}.json`;
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const getCurrentTemplate = (): Template =>
    designer.current?.getTemplate() ?? template;

  const openPdfBlob = (pdf: Uint8Array) => {
    const blob = new Blob([pdf as any], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url);
  };

  const generatePdf = async () => {
    try {
      const currentTemplate = getCurrentTemplate();
      const displayData = buildDisplayData(bank ?? null);
      const inputs = getInputFromTemplate(currentTemplate).map(input => {
        const result = { ...input };
        Object.keys(result).forEach(key => {
          if (displayData[key] !== undefined) {
            result[key] = displayData[key];
          }
        });
        return result;
      });
      const pdf = await generate({
        template: currentTemplate,
        plugins: plugins,
        inputs,
        options: { font },
      });
      openPdfBlob(pdf);
    } catch (e: any) {
      error(e?.message ?? 'Failed to generate PDF');
    }
  };

  const handleCreateElement = () => {
    if (!ref.current) {
      return;
    }
    const element = baseElements.find(it => it.name == ref.current.value);
    if (element) {
      setTemplate(its => {
        const newTemplate = {
          ...its,
          schemas: its.schemas.map((schemaArray, index) =>
            index === 0 ? [...schemaArray, element] : schemaArray,
          ),
        };
        designer.current?.updateTemplate(newTemplate);
        return newTemplate;
      });
    }
  };

  const handleSave = async () => {
    if (!bankId) {
      return;
    }
    await saveCheckTemplate(
      bankId,
      fromDisplayTemplate(getCurrentTemplate()),
      checkType,
    );
    success(t('checkDesigner.actions.saveComplete'));
  };

  if (template == null) {
    return <Loader />;
  }

  //@ts-ignore
  if (designerRef.current && designerRef != prevDesignerRef) {
    if (prevDesignerRef && designer.current) {
      designer.current.destroy();
    }

    buildDesigner();
    //@ts-ignore
    setPrevDesignerRef(designerRef);
  }

  const baseElements = defaultTemplate?.schemas
    .flatMap(its => its)
    .filter(it => {
      const exists = template.schemas.some(t =>
        t.some(e => e.name === it.name),
      );
      return !exists;
    });

  const handleTypeChange = (event: SelectChangeEvent) => {
    setCheckType(event.target.value as PrintType);
  };

  const disabledAddField =
    !baseElements.length ||
    loadCheckTemplatePending ||
    saveCheckTemplatePending;

  return (
    <>
      <div
        style={{
          height: controllerHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 1rem',
        }}
      >
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button
            style={actionButtonStyle}
            variant="outlined"
            size="small"
            component="label"
            disabled={loadCheckTemplatePending || saveCheckTemplatePending}
            startIcon={<RefreshCircle variant="TwoTone" size="20" />}
          >
            {t('checkDesigner.actions.changePDF')}
            <input type="file" hidden accept=".pdf" onChange={changeBasePdf} />
          </Button>

          <Button
            style={actionButtonStyle}
            variant="outlined"
            component="label"
            size="small"
            disabled={loadCheckTemplatePending || saveCheckTemplatePending}
            startIcon={<DocumentUpload variant="TwoTone" size="20" />}
          >
            {t('checkDesigner.actions.loadTemplate')}
            <input type="file" hidden accept=".json" onChange={loadTemplate} />
          </Button>

          <Button
            style={actionButtonStyle}
            variant="outlined"
            size="small"
            color="primary"
            onClick={downloadTemplate}
            disabled={loadCheckTemplatePending || saveCheckTemplatePending}
            startIcon={<DocumentDownload variant="TwoTone" size="20" />}
          >
            {t('checkDesigner.actions.downloadTemplate')}
          </Button>

          <Button
            style={actionButtonStyle}
            variant="outlined"
            size="small"
            color="primary"
            startIcon={<Eye variant="TwoTone" size="20" />}
            disabled={loadCheckTemplatePending || saveCheckTemplatePending}
            onClick={generatePdf}
          >
            {t('checkDesigner.actions.previewPDF')}
          </Button>
        </div>
        <div>
          <Select
            size="small"
            inputRef={ref}
            style={{ marginRight: '1rem' }}
            variant="standard"
            onChange={handleTypeChange}
            fullWidth
            disabled={loadCheckTemplatePending || saveCheckTemplatePending}
            value={checkType}
            labelId="demo-select-small-label"
            id="demo-select-small"
            displayEmpty
            sx={{
              width: '7rem',
              '& legend': { display: 'none' },
              '& fieldset': { top: 0 },
            }}
          >
            <MenuItem key={PrintType.Check} value={PrintType.Check}>
              Check
            </MenuItem>
            <MenuItem key={PrintType.WhitePaper} value={PrintType.WhitePaper}>
              WhitePaper
            </MenuItem>
          </Select>

          <Select
            size="small"
            inputRef={ref}
            variant="standard"
            fullWidth
            labelId="demo-select-small-label"
            id="demo-select-small"
            disabled={disabledAddField}
            displayEmpty
            sx={{
              width: '10rem',
              '& legend': { display: 'none' },
              '& fieldset': { top: 0 },
            }}
          >
            {baseElements.map(it => (
              <MenuItem key={it.name} value={it.name}>
                {it.name}
              </MenuItem>
            ))}
          </Select>

          <Tooltip title={t('checkDesigner.actions.addField')}>
            <IconButton
              onClick={handleCreateElement}
              disabled={disabledAddField}
            >
              <Add variant="TwoTone" size="20" />
            </IconButton>
          </Tooltip>

          <Tooltip title={t('checkDesigner.actions.saveTemplate')}>
            <IconButton
              disabled={loadCheckTemplatePending || saveCheckTemplatePending}
              onClick={handleSave}
            >
              <SaveIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      {loadCheckTemplatePending && <Loader />}
      <div
        ref={designerRef}
        style={{
          width: '100%',
          display: loadCheckTemplatePending ? 'none' : 'block',
          height: `calc(100vh - ${headerHeight + controllerHeight}px)`,
        }}
      />
    </>
  );
};
